import React, { Component } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import Axios from 'axios';
import moment from 'moment';

import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import Popover from 'react-bootstrap/lib/Popover';
import { FaInfoCircle } from 'react-icons/fa';
import SimplePanel from '@/shared/components/SimplePanel';
import SignalRService from '@/shared/services/SignalRService';
import { CanRead } from '@/shared/components/security';
import AuthService from '@/shared/services/AuthService';
import DataCacheService from '@/shared/services/DataCacheService';
import intlShape from '@/shared/utils/intlShape';
import { ScrollableTable, ColumnHeader } from '../../shared/components/table';
import JobOverviewRow from './JobOverviewRow';
import api from '../../shared/api';
import config from '../../config';
import messages from './jobMessages';
import JobStatus from './JobStatus';
import getLabelForJobEntity from './getLabelForJobEntity';
import JobOverviewFilters, { defaultFilters } from './JobOverviewFilters';
import SyncOverviewContainer from './SyncOverviewContainer';

const SessionStorageKey = 'JobOverviewParams';
class JobOverviewContainer extends Component {
  static propTypes = {
    intl: intlShape.isRequired,
  };
  constructor(props) {
    super(props);
    const { overviewFilters: sessionFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};
    if (sessionFilters.dateFrom) {
      sessionFilters.dateFrom = moment.utc(sessionFilters.dateFrom);
    }
    if (sessionFilters.dateUntil) {
      sessionFilters.dateUntil = moment.utc(sessionFilters.dateUntil);
    }
    this.defaultFiltersWithUserId = {
      ...defaultFilters,
      userId: AuthService.isSupport() ? null : AuthService.getUserId(),
    };

    this.state = {
      items: [],
      currentSearchParams: {},
      activeTab: 'jobs', // 'jobs' or 'sync'
      initialSearchParams: {
        // Set default filters
        sortKey: 'createdAt',
        desc: true,
        pageSize: 50,
        overviewFilters: {
          ...this.defaultFiltersWithUserId,
          ...sessionFilters,
        },
        // Override with session storage params
        ...sessionParams,
      },
    };
  }

  componentDidMount() {
    SignalRService.subscribe('jobUpdated', this.jobUpdateHandler);
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('jobUpdated', this.jobUpdateHandler);
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  jobUpdateHandler = job => this.updateJob(job);

  updateJob(job) {
    this.setState(state => {
      const { items } = state;
      const index = items.findIndex(x => x.id === job.id);
      if (index !== -1) {
        items[index] = job;
        return {
          ...state,
          items,
        };
      }
      return {
        ...state,
        items: [job, ...state.items],
      };
    });
  }

  getEntityColumnLabel = job => {
    const { intl } = this.props;
    const isPlural = job.totalEntries > 1;

    const label = getLabelForJobEntity(intl, job.jobType, isPlural);
    return label;
  };

  filtersChanged = filters => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.currentSearchParams,
        page: 0,
        overviewFilters: filters,
      },
    }));
  };
  resetFilters = () => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.currentSearchParams,
        page: 0,
        overviewFilters: { ...this.defaultFiltersWithUserId },
        filter: {},
        // forces refresh
        random: new Date().getTime(),
      },
    }));
  };

  handleTabChange = tab => {
    this.setState({ activeTab: tab });
  };

  renderTitle = () => {
    const { intl } = this.props;
    const { activeTab } = this.state;
    
    // Check if company has any sync-supported connection platforms
    const shouldShowSyncOverview = DataCacheService.shouldSeeSyncOverview(null);

    if (!shouldShowSyncOverview) {
      // Show original title with info icon
      return (
        <span>
          <FormattedMessage {...messages.GlobalActions} />
          <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="bottom"
            rootClose
            overlay={
              <Popover id="popover-trigger-hover-focus">
                {intl.formatMessage(messages.JobOverviewExplanation)}
              </Popover>
            }
          >
            <span className="margin10L explanation-icon">
              <FaInfoCircle />
            </span>
          </OverlayTrigger>
        </span>
      );
    }

    // Show tabs when a supported sync connection exists
    return (
      <span>
        <span
          style={{
            cursor: 'pointer',
            color: activeTab === 'jobs' ? '#333' : '#999',
            fontWeight: activeTab === 'jobs' ? 'bold' : 'normal',
            marginRight: '20px',
          }}
          onClick={() => this.handleTabChange('jobs')}
        >
          {' '}
          <FormattedMessage {...messages.GlobalActions} />
          <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="bottom"
            rootClose
            overlay={
              <Popover id="popover-trigger-hover-focus">
                {intl.formatMessage(messages.JobOverviewExplanation)}
              </Popover>
            }
          >
            <span className="margin10L explanation-icon">
              <FaInfoCircle />
            </span>
          </OverlayTrigger>
        </span>
        <span
          style={{
            cursor: 'pointer',
            color: activeTab === 'sync' ? '#333' : '#999',
            fontWeight: activeTab === 'sync' ? 'bold' : 'normal',
          }}
          onClick={() => this.handleTabChange('sync')}
        >
          <FormattedMessage {...messages.Synchronizations} />
        </span>
      </span>
    );
  };

  renderJobOverview = () => {
    const { intl } = this.props;
    const { initialSearchParams, currentSearchParams, items } = this.state;
    const hasColumnFilters =
      !!currentSearchParams &&
      !!currentSearchParams.filter &&
      Object.keys(currentSearchParams.filter).length !== 0;

    return (
      <>
        <CanRead on="settings">
          <JobOverviewFilters
            filters={initialSearchParams.overviewFilters}
            onFilterChange={filters => this.filtersChanged(filters)}
            onResetFilters={this.resetFilters}
            hasColumnFilters={hasColumnFilters}
          />
        </CanRead>
        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => (
            <JobOverviewRow
              key={item.id}
              item={item}
              intl={intl}
              entityLabel={this.getEntityColumnLabel(item)}
            />
          )}
          noDataLabel={<FormattedMessage {...messages.NoJobsFound} />}
        >
          <ColumnHeader title={intl.formatMessage(messages.JobType)} sortKey="jobType" />
          <ColumnHeader />
          <ColumnHeader
            title={intl.formatMessage(messages.JobStatus)}
            sortKey="status"
            filter={{
              type: 'items',
              key: `status`,
              items: [0, 1, 2, 3],
              renderItem: item => <JobStatus status={item} intl={intl} />,
            }}
          />
          <ColumnHeader title={intl.formatMessage(messages.CreatedBy)} sortKey="createdBy" />
          <ColumnHeader title={intl.formatMessage(messages.CreatedAt)} sortKey="createdAt" />
          <ColumnHeader title={intl.formatMessage(messages.CompletedAt)} sortKey="completedAt" />
          <ColumnHeader />
        </ScrollableTable>
      </>
    );
  };

  loadItems = searchParams => {
    sessionStorage.setItem(
      SessionStorageKey,
      JSON.stringify({
        desc: searchParams.desc,
        sortKey: searchParams.sortKey,
        overviewFilters: searchParams.overviewFilters,
        filter: searchParams.filter,
      }),
    );
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }
    this.cancelToken = Axios.CancelToken.source();

    const search = JSON.stringify(searchParams.search || {});
    const filter = JSON.stringify(searchParams.filter || {});
    const endpoint = `${config.appUrls.jobs}/api/Jobs/overview`;

    return api.base
      .post(endpoint, searchParams.overviewFilters, {
        params: {
          desc: searchParams.desc,
          sort: searchParams.sortKey,
          page: searchParams.page,
          take: searchParams.pageSize,
          search,
          filter,
        },
        cancelToken: this.cancelToken.token,
      })
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          items: searchParams.page > 0 ? [...state.items, ...data] : [...data],
          currentSearchParams: searchParams,
        }));
        return data;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
        // Return empty array for cancelled requests so ScrollableTable can handle it properly
        return [];
      });
  };

  render() {
    const { intl } = this.props;
    const { activeTab } = this.state;
 
    // Check if company has any sync-supported connection platforms
    const shouldShowSyncOverview = DataCacheService.shouldSeeSyncOverview(null);

    return (
      <div>
        <SimplePanel title={this.renderTitle()}>
          {!shouldShowSyncOverview || activeTab === 'jobs' ? (
            this.renderJobOverview()
          ) : (
            <SyncOverviewContainer intl={intl} />
          )}
        </SimplePanel>
      </div>
    );
  }
}

export default injectIntl(JobOverviewContainer);
